import { type SuretyQuoteId } from "@inrev/common";
import { useCallback, useReducer } from "react";

type ModalState =
	| { step: "accountAndAgency" }
	| { step: "principalAndType" }
	| { step: "files" }
	| { step: "commercialInfo" }
	| { step: "commercialSummary" }
	| { step: "submitting" }
	| { step: "resolving"; quoteId: SuretyQuoteId }
	| { step: "waiting"; quoteId: SuretyQuoteId }
	| { step: "outcome"; quoteId: SuretyQuoteId; outcome: "approved" | "review" };

type ModalAction =
	| { type: "goToPrincipalAndType" }
	| { type: "goToFiles" }
	| { type: "goToCommercialInfo" }
	| { type: "goToCommercialSummary" }
	| { type: "goToSubmitting" }
	| { type: "goToResolving"; quoteId: SuretyQuoteId }
	| { type: "goToWaiting" }
	| { type: "goToOutcome"; outcome: "approved" | "review" }
	| { type: "goToSubmitError" }
	| { type: "reset"; startAtAccountSelection?: boolean };

const getInitialModalState = (startAtAccountSelection?: boolean): ModalState =>
	startAtAccountSelection ? { step: "accountAndAgency" } : { step: "principalAndType" };

const modalReducer = (state: ModalState, action: ModalAction): ModalState => {
	switch (action.type) {
		case "goToPrincipalAndType":
			if (
				state.step !== "accountAndAgency" &&
				state.step !== "files" &&
				state.step !== "commercialInfo"
			) {
				return state;
			}
			return { step: "principalAndType" };
		case "goToFiles":
			if (state.step !== "principalAndType") return state;
			return { step: "files" };
		case "goToCommercialInfo":
			if (state.step !== "principalAndType" && state.step !== "commercialSummary") return state;
			return { step: "commercialInfo" };
		case "goToCommercialSummary":
			if (state.step !== "commercialInfo") return state;
			return { step: "commercialSummary" };
		case "goToSubmitting":
			if (state.step !== "files") return state;
			return { step: "submitting" };
		case "goToResolving":
			if (state.step !== "submitting") return state;
			return { step: "resolving", quoteId: action.quoteId };
		case "goToWaiting":
			if (state.step !== "resolving") return state;
			return { step: "waiting", quoteId: state.quoteId };
		case "goToOutcome":
			if (state.step !== "resolving" && state.step !== "waiting") return state;
			return { step: "outcome", quoteId: state.quoteId, outcome: action.outcome };
		case "goToSubmitError":
			if (state.step !== "submitting") return state;
			return { step: "files" };
		case "reset":
			return getInitialModalState(action.startAtAccountSelection);
	}
};

type BondRequestSubmissionFlow = {
	state: ModalState;
	goToPrincipalAndType: () => void;
	goToFiles: () => void;
	goToCommercialInfo: () => void;
	goToCommercialSummary: () => void;
	goToSubmitting: () => void;
	goToResolving: (quoteId: SuretyQuoteId) => void;
	goToWaiting: () => void;
	goToOutcome: (outcome: "approved" | "review") => void;
	goToSubmitError: () => void;
	reset: () => void;
};

export const useBondRequestSubmissionFlow = (
	startAtAccountSelection?: boolean,
): BondRequestSubmissionFlow => {
	const [state, dispatch] = useReducer(modalReducer, getInitialModalState(startAtAccountSelection));
	const goToPrincipalAndType = useCallback(() => dispatch({ type: "goToPrincipalAndType" }), []);
	const goToFiles = useCallback(() => dispatch({ type: "goToFiles" }), []);
	const goToCommercialInfo = useCallback(() => dispatch({ type: "goToCommercialInfo" }), []);
	const goToCommercialSummary = useCallback(() => dispatch({ type: "goToCommercialSummary" }), []);
	const goToSubmitting = useCallback(() => dispatch({ type: "goToSubmitting" }), []);
	const goToResolving = useCallback(
		(quoteId: SuretyQuoteId) => dispatch({ type: "goToResolving", quoteId }),
		[],
	);
	const goToWaiting = useCallback(() => dispatch({ type: "goToWaiting" }), []);
	const goToOutcome = useCallback(
		(outcome: "approved" | "review") => dispatch({ type: "goToOutcome", outcome }),
		[],
	);
	const goToSubmitError = useCallback(() => dispatch({ type: "goToSubmitError" }), []);
	const reset = useCallback(
		() => dispatch({ type: "reset", startAtAccountSelection }),
		[startAtAccountSelection],
	);

	return {
		state,
		goToPrincipalAndType,
		goToFiles,
		goToCommercialInfo,
		goToCommercialSummary,
		goToSubmitting,
		goToResolving,
		goToWaiting,
		goToOutcome,
		goToSubmitError,
		reset,
	};
};
